import { Fragment, useMemo } from 'react';
import type { ChatResponse } from '@magic/schemas/chat';
import styled from 'styled-components';

import { Message, Typing } from './message';
import { Timestamp } from './timestamp';
import './messages.css';

const StyledMessageDivider = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  time {
    position: relative;
    padding: 0 var(--size-spacing-02);
    font: var(--text-sm-regular);
    color: var(--neutral-text-low-emphasis);
    background: var(--neutral-surface);
  }

  hr {
    position: absolute;
    top: 50%;
    width: 100%;
    height: 0;
    border: 0;
    border-bottom: 1px solid var(--neutral-border);
  }
`;

const isSameDay = (a: Date, b: Date) =>
  new Date(a).setHours(0, 0, 0, 0) === new Date(b).setHours(0, 0, 0, 0);

export interface MessagesDividerProps {
  timestamp: Date;
  previous?: Date;
}

const MessagesDivider = ({
  timestamp,
  previous = new Date(),
}: MessagesDividerProps) => {
  return isSameDay(timestamp, previous) ? null : (
    <StyledMessageDivider>
      <hr />
      <Timestamp value={timestamp} />
    </StyledMessageDivider>
  );
};

const MessagesContainer = styled.div`
  position: relative;
  flex: 1;
`;

const MessagesWrapper = styled.div`
  position: absolute;
  padding: var(--size-spacing-04);
  border: none;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  gap: var(--size-spacing-05);
  overflow-y: auto;
  padding-top: 78px;
  padding-bottom: 96px;
  > div {
    width: 100%;
    max-width: 780px;
    margin: 0 auto;
  }
`;

export type MessageRenderer = (
  message: ChatResponse['messages'][number]
) => JSX.Element | null;

interface MessagesProps {
  isLoading?: boolean;
  messages: ChatResponse['messages'];
  renderer?: MessageRenderer;
}

export const Messages = ({
  isLoading = false,
  messages = [],
  renderer: customRenderer,
}: MessagesProps) => {
  const defaultRenderer = useMemo<MessageRenderer>(
    () =>
      ({ role, content, createdAt }) => {
        if (!content) {
          return null;
        }

        switch (role) {
          case 'assistant':
          case 'user':
            return (
              <Message role={role} timestamp={createdAt}>
                {content}
              </Message>
            );
          default:
            return null;
        }
      },
    []
  );

  const renderer = useMemo<MessageRenderer>(
    () => (message) => {
      const custom = customRenderer && customRenderer(message);
      if (custom) return custom;
      return defaultRenderer(message);
    },
    [defaultRenderer, customRenderer]
  );

  return (
    <MessagesContainer>
      <MessagesWrapper>
        {isLoading && <Typing />}
        {messages.map((message, index) => (
          <Fragment key={`${message.role}_${message.createdAt}`}>
            <MessagesDivider
              timestamp={message.createdAt!}
              previous={messages[index + 1]?.createdAt}
            />
            {renderer(message)}
          </Fragment>
        ))}
      </MessagesWrapper>
    </MessagesContainer>
  );
};
